import _ from 'lodash';
import config from 'config';

import WebStorage from '../../data-providers/webstorage-polyfill';
import Analytics from '../../analytics';

export default [
  '$q', '$scope', '$state', '$timeout', 'MarketplaceService', 'CampaignService', 'ErrorHandler', 'UserDataService',
  class MarketplaceController {
    constructor($q, $scope, $state, $timeout, marketplaceService, CampaignService, errorHandler, UserDataService) {
      this._$q = $q;
      this._$scope = $scope;
      this._$state = $state;
      this._$timeout = $timeout;
      this._marketplaceService = marketplaceService;
      this._campaignService = CampaignService;
      this._errorHandler = errorHandler;
      this._userDataService = UserDataService;
      this.storage = new WebStorage();
      this.analytics = new Analytics();

      this._initState();
      this._fetchData();
      this._trackCSDC();
      window.scrollTo(0, 0);
    }

    inputChanged() {
      this.page = 1;
      this._refresh({ page: this.page === 1 ? null : this.page, search: this.search ? this.search : null });
      this._performSearch();
    }

    changeCategory() {
      this.page = 1;
      this._refresh({ page: this.page === 1 ? null : this.page, category: this.category === 'ALL' ? null : this.category, sub_category: null });
      this.subCategory = 'ALL';
      let categoryObj = _.find(this.categories, (category) => category.category === this.category);
      this.subCategories = categoryObj ? categoryObj.subCategories : null;
      this._performSearch();
    }

    changeSubCategory() {
      this.page = 1;
      this._refresh({ page: this.page === 1 ? null : this.page, sub_category: this.subCategory === 'ALL' ? null : this.subCategory });
      this._performSearch();
    }

    changeProductType() {
      this.page = 1;
      this._refresh({ page: this.page === 1 ? null : this.page, product_type: this.productType === '0' ? null : this.productType });
      this._performSearch();
    }

    sliderChange() {
      this.page = 1;
      this._refresh({ page: this.page === 1 ? null : this.page, min_price: this.minPrice <= 0 ? null : this.minPrice, max_price: this.maxPrice >= 80 ? null : this.maxPrice });
      this._performSearch();
    }

    pageChanged() {
      this._refresh({ page: this.page === 1 ? null : this.page });
      this._performSearch();
    }

    scrollToTop(event) {
      event.preventDefault();
      angular.element('html, body').animate({
        scrollTop: 0
      }, 200);
    }

    isUserAdmin() {
      return this._userDataService.isInAdminRole();
    }

    hideCampaignFromMarketplace(event, id) {
      event.preventDefault();
      let lock = !this[id + '-hide'];
      this._campaignService.toggleCampaignLockFromMarketplace(id, lock, !lock)
        .then(() => {
          this[id + '-hide'] = lock;
        })
        .catch(err => this._marketplaceError(err, 'Encountered an error while attempting to lock/unlock campaign from marketplace.'));
    }

    insertUTM(uri) {
      let splitUri = uri.split('#');
      return splitUri.length > 1 ? `${splitUri[0]}?utm_source=marketplace#${splitUri[1]}` : `${uri}?utm_source=marketplace`;
    }

    _initState() {
      this.processing = true;
      this.campaigns = [];
      this.totalCampaigns = 0;
      this.searchTimeout = null;
      this.priceTimeout = null;
      this.showFilter = false;
      this.maxPaginationPages = 5;
      this.itemsPerPage = config.Marketplace.LIMIT;

      this.page = parseInt(this._$state.params.page) > 0 ? parseInt(this._$state.params.page) : 1;
      this.search = this._$state.params.search;
      this.category = this._$state.params.category || 'ALL';
      this.subCategory = this._$state.params.sub_category || 'ALL';
      this.productType = this._$state.params.product_type || '0';

      this._initSliderState();
    }

    _initSliderState() {
      let min_price = parseInt(this._$state.params.min_price) || null,
        max_price = parseInt(this._$state.params.max_price) || null;

      this.minPrice = min_price > config.Marketplace.MIN_PRICE && min_price <= config.Marketplace.MAX_PRICE ? min_price : config.Marketplace.MIN_PRICE;
      this.maxPrice = max_price > config.Marketplace.MIN_PRICE && max_price < config.Marketplace.MAX_PRICE ? max_price : config.Marketplace.MAX_PRICE;
      this.sliderOptions = { floor: config.Marketplace.MIN_PRICE, ceil: config.Marketplace.MAX_PRICE, translate: (value) => `$${value}` };

      if (this.minPrice && this.maxPrice && this.minPrice > this.maxPrice) this.minPrice = this.maxPrice;

      this._$scope.$on('slideEnded', () => this.sliderChange());

      this._refresh({
        min_price: this.minPrice === config.Marketplace.MIN_PRICE ? null : min_price,
        max_price: this.maxPrice === config.Marketplace.MAX_PRICE ? null : max_price,
        page: this.page === 1 ? null : this.page
      });
    }

    _fetchData() {
      let categoriesPromise = this._marketplaceService.categoriesTree()
        .then((res) => this._initCategories(res.categories));

      let productTypesPromise = this._marketplaceService.productTypes()
        .then((res) => this._initProductTypes(res));

      this._$q.all([categoriesPromise, productTypesPromise])
        .then(() => this._performSearch())
        .catch(err => this._marketplaceError(err))
        .finally(() => {
          this.processing = false;
          this.initialized = true;
        });
    }

    _initCategories(categories) {
      let categoriesArray = [{ category: 'ALL', subCategories: [] }];

      for (let key in categories) {
        categories[key].unshift({ name: 'ALL' });
        categoriesArray.push({ category: key, subCategories: categories[key] });
      }
      this.categories = categoriesArray;

      if (this.category !== 'ALL' && !_.find(this.categories, category => category.category === this.category)) {
        this.category = 'ALL';
        this._refresh({ category: null, sub_category: null });
      }

      let categoryObj = _.find(this.categories, category => category.category === this.category);
      this.subCategories = _.get(categoryObj, 'subCategories');

      if (this.subCategory !== 'ALL' && !_.find(this.subCategories, category => category.name === this.subCategory)) {
        this.subCategory = 'ALL';
        this._refresh({ sub_category: null });
      }
    }

    _initProductTypes(res) {
      let productTypeArray = [{ id: '0', type: 'ALL' }];

      for (let key in res) {
        productTypeArray.push({ id: key, type: res[key] });
      }
      this.productTypes = productTypeArray;

      if (this.productType !== '0' && !_.find(this.productTypes, type => type.id === this.productType)) {
        this.productType = '0';
        this._refresh({ product_type: null });
      }
    }

    _performSearch() {
      let promise;

      if (this.subCategory && this.subCategory !== 'ALL') {
        promise = this._marketplaceService.searchMarketplace(this.page, this.productType, this.minPrice, this.maxPrice, this.subCategory, this.search)
      } else {
        promise = this._marketplaceService.searchMarketplace(this.page, this.productType, this.minPrice, this.maxPrice, this.category, this.search)
      }

      this.processing = true;
      promise.then(res => {
          this.campaigns = res.results;
          this.totalCampaigns = res.total_hits;
          window.scrollTo(0, 0);
        })
        .catch(err => this._marketplaceError(err))
        .finally(() => this.processing = false);

      return promise;
    }

    _marketplaceError(err, message = 'Sorry we encountered a problem searching the marketplace, please try again.') {
      return this._errorHandler.throwError('Marketplace Error', message, {
        query: this.search,
        category: this.category,
        subCategory: this.subCategory,
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
        productType: this.productType,
        paginationPage: this.page,
        campaignsLength: this.campaigns ? this.campaigns.length : 'null',
        rawError: err
      });
    }

    _refresh(params, notify = false) {
      this._$state.go('marketplace-page', params, { notify });
    }

    _trackCSDC() {
      if (this._$state.params.csdc && this._$state.params.csdc_email) {
        let prevDiscountHash = `csdc:${this._$state.params.csdc}`,
          hasPrevDiscount = this.storage.getLocalItem(prevDiscountHash);

        if (!hasPrevDiscount) {
          this.storage.setLocalItem(prevDiscountHash, true);
          this.analytics.applyCoupon(this._$state.params.csdc);
        }
      }
    }
  }
];


// WEBPACK FOOTER //
// ./source/client/pages/marketplace/marketplace.ctrl.js